import { useState, useEffect } from 'react';
import { getContractReadOnly } from '../utils/web3';
import { ethers } from 'ethers';

export const useUserData = (account) => {
  const [userId, setUserId] = useState(null);
  const [userData, setUserData] = useState(null);
  const [packages, setPackages] = useState([]);
  const [incomeBreakdown, setIncomeBreakdown] = useState(null);
  const [claimableIncome, setClaimableIncome] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [activities, setActivities] = useState([]);
  const [poolEligibility, setPoolEligibility] = useState({});
  const [royaltyEligible, setRoyaltyEligible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);

  useEffect(() => {
    if (account) {
      fetchUserData();
    } else {
      resetData();
    }
  }, [account]);

  const resetData = () => {
    setUserId(null);
    setUserData(null);
    setPackages([]);
    setIncomeBreakdown(null);
    setClaimableIncome(null);
    setReferrals([]);
    setActivities([]);
    setPoolEligibility({});
    setRoyaltyEligible(false);
    setIsRegistered(false);
  };

  const fetchUserData = async () => {
    setIsLoading(true);
    try {
      const contract = getContractReadOnly();
      if (!contract || !account) {
        console.log('Contract or account not available');
        setIsRegistered(false);
        setIsLoading(false);
        return;
      }

      let userIdNum = 0;
      
      try {
        const userIdBN = await contract.addressToId(account);
        userIdNum = userIdBN.toNumber();
      } catch (e) {
        console.log('addressToId call failed - contract might not be deployed:', e.message);
        // Contract might not be deployed, treat as not registered
        setIsRegistered(false);
        setIsLoading(false);
        return;
      }

      if (userIdNum === 0) {
        setIsRegistered(false);
        setIsLoading(false);
        return;
      }

      setUserId(userIdNum);
      setIsRegistered(true);

      const [details, packagesData, income, claimable, referralIds] = await Promise.all([
        contract.getUserDetails(userIdNum),
        contract.getUserPackages(userIdNum),
        contract.getUserIncomeBreakdown(userIdNum),
        contract.getUserTotalClaimableIncome(userIdNum),
        contract.getUserReferrals(userIdNum)
      ]);

      setUserData({
        account: details[0],
        id: details[1].toNumber(),
        referrer: details[2].toNumber(),
        isActive: details[3],
        isAdmin: details[4],
        totalDirectReferrals: details[5].toNumber(),
        claimableAmount: ethers.utils.formatUnits(details[6], 18),
        totalClaimed: ethers.utils.formatUnits(details[7], 18),
        totalIncome: ethers.utils.formatUnits(details[8], 18)
      });

      // Fetch package details and eligibility
      const packagesArray = [];
      const eligibility = {};
      
      for (let i = 0; i < packagesData.length; i++) {
        const pkgDetails = await contract.getUserPackageDetails(userIdNum, i + 1);
        const isActive = pkgDetails[0];
        
        packagesArray.push({
          id: i + 1,
          isActive: isActive,
          activationTime: pkgDetails[1].toNumber(),
          totalInvested: ethers.utils.formatUnits(pkgDetails[2], 18),
          totalEarned: ethers.utils.formatUnits(pkgDetails[3], 18),
          ceilingLimit: ethers.utils.formatUnits(pkgDetails[4], 18),
          remainingLimit: ethers.utils.formatUnits(pkgDetails[5], 18),
          unlimitedCeiling: pkgDetails[6]
        });

        // Check pool eligibility for packages 2-10
        if (i >= 1 && isActive) {
          try {
            const poolEligible = await contract.poolEligibility(userIdNum, i + 1);
            if (poolEligible) {
              const claimablePool = await contract.getUserClaimablePoolIncome(userIdNum, i + 1);
              eligibility[i + 1] = {
                eligible: true,
                claimable: ethers.utils.formatUnits(claimablePool, 18)
              };
            }
          } catch (e) {
            console.log('Pool eligibility check failed for package', i + 1);
          }
        }
      }
      
      setPackages(packagesArray);
      setPoolEligibility(eligibility);

      // Check royalty eligibility
      try {
        const royaltyElig = await contract.royaltyEligibility(userIdNum);
        setRoyaltyEligible(royaltyElig);
      } catch (e) {
        console.log('Royalty check failed');
      }

      setIncomeBreakdown({
        directReferralIncome: ethers.utils.formatUnits(income[0], 18),
        levelIncome: ethers.utils.formatUnits(income[1], 18),
        poolIncome: ethers.utils.formatUnits(income[2], 18),
        royaltyIncome: ethers.utils.formatUnits(income[3], 18),
        totalIncome: ethers.utils.formatUnits(income[4], 18),
        claimableAmount: ethers.utils.formatUnits(income[5], 18),
        totalClaimed: ethers.utils.formatUnits(income[6], 18)
      });

      setClaimableIncome({
        regularIncome: ethers.utils.formatUnits(claimable[0], 18),
        poolIncome: ethers.utils.formatUnits(claimable[1], 18),
        royaltyIncome: ethers.utils.formatUnits(claimable[2], 18),
        totalClaimable: ethers.utils.formatUnits(claimable[3], 18)
      });

      setReferrals(referralIds.map(id => id.toNumber()));

      // Fetch activities
      try {
        const userActivities = await contract.getUserActivities(userIdNum);
        const formattedActivities = userActivities.map(act => ({
          id: act.id.toNumber(),
          userId: act.userId.toNumber(),
          activityType: act.activityType.toNumber ? act.activityType.toNumber() : Number(act.activityType),
          amount: ethers.utils.formatUnits(act.amount, 18),
          packageId: act.packageId.toNumber ? act.packageId.toNumber() : Number(act.packageId),
          fromId: act.fromId.toNumber ? act.fromId.toNumber() : Number(act.fromId),
          timestamp: act.timestamp.toNumber ? act.timestamp.toNumber() : Number(act.timestamp)
        })).sort((a, b) => b.timestamp - a.timestamp);
        setActivities(formattedActivities);
      } catch (e) {
        console.log('Activities fetch failed:', e);
        setActivities([]);
      }

    } catch (error) {
      console.error('Error fetching user data:', error);
      // On error, assume not registered
      setIsRegistered(false);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshData = () => {
    if (account) fetchUserData();
  };

  return {
    userId,
    userData,
    packages,
    incomeBreakdown,
    claimableIncome,
    referrals,
    activities,
    poolEligibility,
    royaltyEligible,
    isLoading,
    isRegistered,
    refreshData
  };
};
